class TestimonialsJudgemePilgrim extends HTMLElement {
  constructor() {
    super();
    this.onReady = this.onReady.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.onReady);
    } else {
      this.onReady();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('DOMContentLoaded', this.onReady);
  }

  get reviewsLimit() {
    const limit = parseInt(this.dataset.reviewsLimit || '10', 10);
    return Number.isNaN(limit) ? 10 : Math.min(12, Math.max(4, limit));
  }

  get minRating() {
    const value = parseInt(this.dataset.minRating || '4', 10);
    return Number.isNaN(value) ? 4 : value;
  }

  get minLength() {
    const value = parseInt(this.dataset.minLength || '30', 10);
    return Number.isNaN(value) ? 30 : value;
  }

  async onReady() {
    const track = this.querySelector('[data-testimonials-track]');
    const source = this.querySelector('[data-judgeme-source]');
    const fallback = this.querySelector('[data-judgeme-fallback]');

    if (!track) return;

    let reviews = [];

    const sourceHtml = source ? source.innerHTML.trim() : '';
    if (sourceHtml) {
      reviews = this.filterReviews(this.parseReviews(sourceHtml));
    }

    if (reviews.length < this.reviewsLimit) {
      const extraHtml = await this.fetchFeaturedCarousel();
      if (extraHtml) {
        reviews = this.filterReviews(this.mergeReviews(reviews, this.parseReviews(extraHtml)));
      }
    }

    if (reviews.length < this.reviewsLimit) {
      const tabHtml = await this.fetchReviewsTab();
      if (tabHtml) {
        reviews = this.filterReviews(this.mergeReviews(reviews, this.parseReviews(tabHtml)));
      }
    }

    reviews = reviews.slice(0, this.reviewsLimit);

    if (!reviews.length) {
      this.showFallback(fallback, track);
      return;
    }

    if (fallback) fallback.hidden = true;
    if (source) source.hidden = true;

    track.innerHTML = reviews.map((review, index) => this.buildSlide(review, index)).join('');

    track.addEventListener('click', (event) => {
      const button = event.target.closest('[data-testimonial-more]');
      if (!button) return;

      const card = button.closest('.testimonial-pilgrim-card');
      const expanded = card.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? 'Leer menos' : 'Leer más';
    });

    requestAnimationFrame(() => {
      this.refreshSlider();
      requestAnimationFrame(() => this.refreshSlider());
    });
  }

  refreshSlider() {
    const sliderComponent = this.querySelector('slider-component');

    if (!sliderComponent || !sliderComponent.resetPages) return;

    sliderComponent.resetPages();
    sliderComponent.update();
  }

  // Una reseña por persona, sin comentarios muy cortos ni datos personales tapados.
  filterReviews(reviews) {
    const seenAuthors = new Set();

    return reviews.filter((review) => {
      const text = `${review.title} ${review.body}`.trim();

      if (review.rating < this.minRating) return false;
      if (review.body.replace(/\s+/g, ' ').length < this.minLength) return false;
      if (/\[\*+\]|@|\+?\d[\d\s-]{7,}\d/.test(text)) return false;

      const authorKey = review.author.trim().toLowerCase();
      if (authorKey) {
        if (seenAuthors.has(authorKey)) return false;
        seenAuthors.add(authorKey);
      }

      return true;
    });
  }

  mergeReviews(existing, incoming) {
    const merged = [...existing];
    const keys = new Set(existing.map((review) => this.reviewKey(review)));

    incoming.forEach((review) => {
      const key = this.reviewKey(review);
      if (!keys.has(key)) {
        keys.add(key);
        merged.push(review);
      }
    });

    return merged;
  }

  reviewKey(review) {
    return `${review.author}|${review.title}|${review.body}`.toLowerCase();
  }

  async fetchFeaturedCarousel() {
    const shopDomain = this.dataset.shopDomain;
    const apiToken = this.dataset.apiToken;

    if (!shopDomain || !apiToken) return '';

    return this.fetchWidget('featured_carousel');
  }

  async fetchReviewsTab() {
    const shopDomain = this.dataset.shopDomain;
    const apiToken = this.dataset.apiToken;

    if (!shopDomain || !apiToken) return '';

    return this.fetchWidget('reviews_tab', {
      per_page: String(this.reviewsLimit),
      review_type: 'product-reviews'
    });
  }

  async fetchWidget(widgetName, extraParams = {}) {
    const shopDomain = this.dataset.shopDomain;
    const apiToken = this.dataset.apiToken;

    if (!shopDomain || !apiToken) return '';

    try {
      const url = new URL(`https://judge.me/api/v1/widgets/${widgetName}`);
      url.searchParams.set('shop_domain', shopDomain);
      url.searchParams.set('api_token', apiToken);

      Object.entries(extraParams).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });

      const response = await fetch(url.toString());

      if (!response.ok) return '';

      const data = await response.json();
      return data.widget || data.html || '';
    } catch (error) {
      return '';
    }
  }

  parseReviews(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = doc.querySelectorAll('.jdgm-carousel-item, .jdgm-rev');

    return Array.from(items)
      .map((item) => {
        const ratingNode =
          item.querySelector('[data-score]') ||
          item.querySelector('.jdgm-carousel-item__rating') ||
          item.querySelector('.jdgm-rev__rating');

        const rating = parseInt(ratingNode?.getAttribute('data-score') || '5', 10);

        const title =
          item.querySelector('.jdgm-carousel-item__review-title')?.textContent?.trim() ||
          item.querySelector('.jdgm-rev__title')?.textContent?.trim() ||
          '';

        const body =
          item.querySelector('.jdgm-carousel-item__review-body')?.textContent?.trim() ||
          item.querySelector('.jdgm-rev__body')?.textContent?.trim() ||
          item.querySelector('.jdgm-carousel-item__review-content')?.textContent?.trim() ||
          '';

        const author =
          item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent?.trim() ||
          item.querySelector('.jdgm-rev__author')?.textContent?.trim() ||
          '';

        const location =
          item.querySelector('.jdgm-rev__location')?.textContent?.trim() ||
          item.querySelector('.jdgm-carousel-item__reviewer-location')?.textContent?.trim() ||
          '';

        return {
          rating: Number.isNaN(rating) ? 5 : Math.min(5, Math.max(1, rating)),
          title,
          body,
          author: author.replace(/^[-–—]\s*/, ''),
          location
        };
      })
      .filter((review) => review.body || review.title);
  }

  buildSlide(review, index) {
    const sectionId = this.dataset.sectionId || 'testimonials';
    const slideId = `Slide-${sectionId}-${index + 1}`;
    const ratingDecimal = 0;
    const titleHtml = review.title
      ? `<p class="testimonial-pilgrim-card__title tw-m-0 tw-break-words tw-font-sans tw-text-[18px] tw-font-semibold tw-leading-normal tw-text-pilgrim-turquesa-900">${this.escapeHtml(review.title)}</p>`
      : '';
    const isLong = review.body.length > 140;
    const bodyHtml = review.body
      ? `<p class="testimonial-pilgrim-card__quote${isLong ? ' testimonial-pilgrim-card__quote--clamped' : ''} tw-m-0 tw-break-words tw-font-sans tw-text-[16px] tw-font-normal tw-leading-normal tw-text-pilgrim-turquesa-900">&ldquo;${this.escapeHtml(review.body)}&rdquo;</p>`
      : '';
    const moreHtml = isLong
      ? `<button type="button" class="testimonial-pilgrim-card__more" data-testimonial-more aria-expanded="false">Leer más</button>`
      : '';
    const authorHtml = review.author
      ? `<p class="testimonial-pilgrim-card__name tw-m-0 tw-break-words tw-font-sans tw-text-[16px] tw-font-semibold tw-leading-normal tw-text-pilgrim-turquesa-900">&mdash; ${this.escapeHtml(review.author)}</p>`
      : '';
    const locationHtml = review.location
      ? `<p class="testimonial-pilgrim-card__city tw-m-0 tw-break-words tw-font-sans tw-text-[16px] tw-font-normal tw-leading-normal tw-text-pilgrim-turquesa-900">${this.escapeHtml(review.location)}</p>`
      : '';

    return `
      <li id="${slideId}" class="testimonials-pilgrim__slide slider__slide" role="listitem">
        <article class="testimonial-pilgrim-card tw-flex tw-h-[343px] tw-w-full tw-shrink-0 tw-items-start tw-rounded-[14px] tw-border-[0.5px] tw-border-solid tw-border-pilgrim-turquesa-300 tw-bg-pilgrim-neutral-0 tw-pt-[50px] tw-pb-[38px] tw-pl-[24px] tw-pr-[22px]">
          <div class="testimonial-pilgrim-card__inner tw-flex tw-w-full tw-max-w-[252px] tw-flex-col tw-gap-[44px]">
            <div class="testimonial-pilgrim-card__content tw-flex tw-flex-col tw-gap-5">
              <div class="testimonials-pilgrim__rating rating" role="img" aria-label="${review.rating} de 5 estrellas">
                <span aria-hidden="true" class="rating-star testimonials-pilgrim__rating-star" style="--rating: ${review.rating}; --rating-max: 5; --rating-decimal: ${ratingDecimal};"></span>
              </div>
              ${titleHtml}
              ${bodyHtml}
              ${moreHtml}
            </div>
            ${authorHtml || locationHtml ? `<div class="testimonial-pilgrim-card__author tw-flex tw-flex-col tw-gap-1">${authorHtml}${locationHtml}</div>` : ''}
          </div>
        </article>
      </li>
    `;
  }

  showFallback(fallback, track) {
    if (fallback) {
      fallback.hidden = false;
      track.innerHTML = '';
      return;
    }

    track.innerHTML = `
      <li class="testimonials-pilgrim__slide testimonials-pilgrim__slide--empty slider__slide" role="listitem">
        <p class="tw-m-0 tw-font-sans tw-text-[16px] tw-text-pilgrim-turquesa-900">
          Activa el carrusel de reseñas destacadas en Judge.me para mostrar testimonios reales.
        </p>
      </li>
    `;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

customElements.define('testimonials-judgeme-pilgrim', TestimonialsJudgemePilgrim);
